import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  useReducedMotion,
} from 'motion/react';
import type { ReactNode, PointerEvent } from 'react';
import { twMerge } from 'tailwind-merge';

type Variant = 'primary' | 'ghost';

type MagneticButtonProps = {
  children: ReactNode;
  href: string;
  variant?: Variant;
  className?: string;
  ariaLabel?: string;
};

/**
 * CTA that leans toward the cursor, with a fill that floods up from the bottom
 * on hover. Continuous pointer values live in motion values (never React
 * state). Under reduced motion the magnetism is off and it is a plain link.
 */
export function MagneticButton({
  children,
  href,
  variant = 'primary',
  className = '',
  ariaLabel,
}: MagneticButtonProps) {
  const reduce = useReducedMotion();
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 200, damping: 15, mass: 0.4 });
  const sy = useSpring(y, { stiffness: 200, damping: 15, mass: 0.4 });
  // The label trails the shell slightly for a two-layer, physical feel.
  const lag = { stiffness: 140, damping: 16, mass: 0.5 };
  const lx = useSpring(
    useTransform(x, (v) => v * 0.4),
    lag
  );
  const ly = useSpring(
    useTransform(y, (v) => v * 0.4),
    lag
  );

  const onMove = (e: PointerEvent<HTMLAnchorElement>) => {
    if (reduce) return;
    const rect = e.currentTarget.getBoundingClientRect();
    x.set((e.clientX - (rect.left + rect.width / 2)) * 0.3);
    y.set((e.clientY - (rect.top + rect.height / 2)) * 0.3);
  };

  const onLeave = () => {
    x.set(0);
    y.set(0);
  };

  const base =
    'group relative isolate inline-flex items-center justify-center overflow-hidden rounded-full px-7 py-3.5 text-[15px] font-medium tracking-tight whitespace-nowrap transition-colors duration-500 active:scale-[0.98]';
  const styles: Record<Variant, string> = {
    primary: 'bg-accent text-on-accent',
    ghost:
      'border border-line-strong bg-base/40 text-fg backdrop-blur-md hover:border-accent hover:text-on-accent',
  };
  const flood: Record<Variant, string> = {
    primary: 'bg-[#8fd3c7]',
    ghost: 'bg-accent',
  };

  return (
    <motion.a
      href={href}
      aria-label={ariaLabel}
      onPointerMove={onMove}
      onPointerLeave={onLeave}
      style={{ x: sx, y: sy }}
      className={twMerge(base, styles[variant], className)}
    >
      <span
        aria-hidden
        className={`absolute inset-0 -z-10 origin-bottom scale-y-0 rounded-[inherit] transition-transform duration-500 ease-out-expo group-hover:scale-y-100 ${flood[variant]}`}
      />
      <motion.span
        style={{ x: lx, y: ly }}
        className="inline-flex items-center gap-2"
      >
        {children}
      </motion.span>
    </motion.a>
  );
}
